import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Column } from "primereact/column";
import localeText from "../../../../locale";
import CategoryService from "../../../../service/CategoryService";
import useAdminList, { expectCode } from "../../hooks/useAdminList";
import useAdminAction from "../../hooks/useAdminAction";
import useCatalogForm from "../../hooks/useCatalogForm";
import { getToken } from "../../utils/adminUtils";
import {
  clearFilled,
  emptyTranslations,
  localizedDescription,
  missingTranslations,
  otherTranslations,
  pickTranslations,
  translationsSearchText,
  uploadCatalogImage,
} from "../../utils/catalogUtils";
import CatalogTable from "../../components/list/CatalogTable";
import CatalogThumb from "../../components/list/CatalogThumb";
import FormDialog from "../../components/form/FormDialog";
import TranslationFields from "../../components/form/TranslationFields";
import ImagePicker from "../../components/form/ImagePicker";

const LIST_PATH = "/dashboard-listcategory";

const loadCategories = async () => expectCode(await new CategoryService().getCategories(), 200001).result;

const buildInitial = (item) => ({
  values: item ? pickTranslations(item) : emptyTranslations(),
  file: null,
  errors: {},
});

const AdminCategories = ({ startCreating = false }) => {
  const { items, loading, loadError, reload } = useAdminList(loadCategories);
  const { run, runningId } = useAdminAction();
  const { form, openCreate, openEdit, close, update } = useCatalogForm({
    startCreating,
    listPath: LIST_PATH,
    buildInitial,
  });

  const rows = useMemo(
    () =>
      items.map((category) => ({
        ...category,
        name: localizedDescription(category),
        subcategoriesCount: category.subCategories?.length || 0,
        searchText: translationsSearchText(category),
      })),
    [items],
  );

  const save = async () => {
    const errors = {};
    missingTranslations(form.values).forEach(({ field }) => {
      errors[field] = localeText("adminFormRequired");
    });
    if (form.mode === "create" && !form.file) errors.image = localeText("pleaseUploadAtLeastOne");
    if (Object.keys(errors).length) {
      update({ errors });
      return;
    }

    const { values, file, item } = form;
    const ok = await run(
      "form",
      async () => {
        const imageid = file ? await uploadCatalogImage(file) : item.imageid;
        const service = new CategoryService();
        const args = [
          getToken(),
          values.descriptionEnglish.trim(),
          values.descriptionItalian.trim(),
          values.descriptionPortuguese.trim(),
          values.descriptionSpanish.trim(),
          imageid,
        ];
        const res =
          form.mode === "create"
            ? await service.postRegisterCategory(...args)
            : await service.postEditCategory(...args, item.id);
        expectCode(res, 200000);
      },
      "adminCatalogSaved",
    );
    if (ok) {
      close();
      reload();
    }
  };

  return (
    <div className="admin-list">
      <FormDialog
        visible={!!form}
        title={localeText(form?.mode === "edit" ? "adminCatalogEditCategory" : "addNewCategory")}
        subtitle={form?.mode === "edit" ? localizedDescription(form.item) : localeText("categoryFields")}
        saving={runningId === "form"}
        onSave={save}
        onCancel={close}
      >
        {form && (
          <>
            <TranslationFields
              idPrefix="category"
              values={form.values}
              errors={form.errors}
              onChange={(values) => update({ values, errors: { ...form.errors, ...clearFilled(values) } })}
              autoFocus
            />
            <ImagePicker
              label={localeText("categoryImage")}
              currentImageId={form.item?.imageid}
              file={form.file}
              required={form.mode === "create"}
              error={form.errors.image}
              onChange={(file) => update({ file, errors: { ...form.errors, image: null } })}
              onError={(message) => update({ errors: { ...form.errors, image: message } })}
            />
          </>
        )}
      </FormDialog>

      <CatalogTable
        rows={rows}
        loading={loading}
        loadError={loadError}
        onReload={reload}
        searchPlaceholder={localeText("adminCatalogSearchName")}
        createLabel={localeText("newCategory")}
        onCreate={openCreate}
        sortField="name"
        minWidth="44rem"
      >
        <Column
          header={localeText("category")}
          field="name"
          body={(category) => (
            <div className="admin-catalog__item">
              <CatalogThumb imageId={category.imageid} />
              <div className="admin-stack">
                <span className="admin-strong">{category.name || "—"}</span>
                <span className="admin-catalog__translations">{otherTranslations(category)}</span>
              </div>
            </div>
          )}
          sortable
        />
        <Column
          header={localeText("adminCatalogSubcategories")}
          field="subcategoriesCount"
          body={(category) => <span className="admin-num">{category.subcategoriesCount}</span>}
          sortable
        />
        <Column
          header={localeText("actions")}
          frozen
          alignFrozen="right"
          headerClassName="admin-list__actions-cell"
          bodyClassName="admin-list__actions-cell"
          body={(category) => (
            <div className="admin-list__header-actions">
              <Link
                className="admin-icon-btn"
                to={`/category?id=${category.id}`}
                target="_blank"
                rel="noopener noreferrer"
                title={localeText("adminCatalogViewInStore")}
                aria-label={localeText("adminCatalogViewInStore")}
              >
                <i className="pi pi-external-link" aria-hidden="true" />
              </Link>
              <button type="button" className="admin-btn" onClick={() => openEdit(category)}>
                <i className="pi pi-pencil" aria-hidden="true" />
                {localeText("edit")}
              </button>
            </div>
          )}
        />
      </CatalogTable>
    </div>
  );
};

export default AdminCategories;
